import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Server } from "@wajeez/wajeez.microservices";
import { setStorageSync } from "@modules/storage/storageHelper";

import {
  addBookmark,
  addEvent,
  createHighlight,
  getContentEntitiesProgress,
  getContentEntityById,
  getContentEntityBySelectorsAndSorters,
  getContentEntityItemsById,
  getContentEntityItemsByPlaylistId,
  getDynamicLink,
  getHighlights,
  getInterests,
  getMoreContentEntityBySelectorsAndSorters,
  getNextContent,
  removeBookmark,
  removeHighlight,
  setInterests,
  searchTracked,
  getContentEntityVoiceOvers,
} from "./thunks";
import { Content, ContentState } from "./types";
import _ from "lodash";

const defaultPageSize = 10;
export const name = "content";

const initialState: ContentState = {
  data: [],
  progresses: [],
  linkedData: {},
  status: "idle",
  player: {
    isShown: false,
    isLoading: false,
    isPlaying: undefined,
    isAutoStart: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    playbackRate: 1,
  },
  selectedPlayerContent: {
    content: {
      id: undefined,
      title: undefined,
      author: undefined,
      thumbnail: undefined,
      contentEntityType: undefined,
      source: undefined,
    },
    chapters: {
      data: [],
      status: "idle",
    },
    playingChapterId: undefined,
    firstPlayingChapterId: undefined,
    isPlayingChapterEnded: false,
    forcedPlayingChapterId: undefined,
  },
  visitedPlayerContent: {
    chapters: {
      data: [],
      status: "idle",
    },
  },
  addEvent: {
    status: "idle",
  },
  getInterests: {
    data: [],
    status: "idle",
  },
  setInterests: {
    status: "idle",
  },
  addBookmark: {
    status: "idle",
  },
  removeBookmark: {
    status: "idle",
  },
  selectedContentHighlights: {
    data: [],
    status: "idle",
  },
  createHighlight: {
    status: "idle",
  },
  removeHighlight: {
    status: "idle",
  },
  dynamicLink: {
    data: null,
    status: "idle",
  },
  nextContent: {
    data: null,
    status: "idle",
  },
  playlistContentItems: {
    data: [],
    status: "idle",
  },
  searchTracked: {
    data: [],
    meta: {
      currentPage: undefined,
      hasNextPage: undefined,
      totalCount: undefined,
    },
    status: "idle",
  },
  contentVoiceOvers: {
    data: null,
    status: "idle",
  },
};

const asyncActions = {
  getContentEntityByIdAsync: createAsyncThunk(
    name + "/getContentEntityById",
    getContentEntityById
  ),
  getContentEntityBySelectorsAndSortersAsync: createAsyncThunk(
    name + "/getContentEntityBySelectorsAndSorters",
    getContentEntityBySelectorsAndSorters
  ),
  getMoreContentEntityBySelectorsAndSortersAsync: createAsyncThunk(
    name + "/getMoreContentEntityBySelectorsAndSorters",
    getMoreContentEntityBySelectorsAndSorters
  ),
  getContentEntitiesProgressAsync: createAsyncThunk(
    name + "/getContentEntitiesProgress",
    getContentEntitiesProgress
  ),
  getSelectedPlayerContentEntityItemsByIdAsync: createAsyncThunk(
    name + "/getSelectedContentEntityItemsById",
    getContentEntityItemsById
  ),
  getContentEntityItemsByIdAsync: createAsyncThunk(
    name + "/getContentEntityItemsById",
    getContentEntityItemsById
  ),
  addEventAsync: createAsyncThunk(name + "/addEvent", addEvent),
  getInterestsAsync: createAsyncThunk(name + "/getInterests", getInterests),
  setInterestsAsync: createAsyncThunk(name + "/setInterests", setInterests),
  addBookmarkAsync: createAsyncThunk(name + "/addBookmark", addBookmark),
  removeBookmarkAsync: createAsyncThunk(
    name + "/removeBookmark",
    removeBookmark
  ),
  getHighlightsAsync: createAsyncThunk(name + "/getHighlights", getHighlights),
  createHighlightAsync: createAsyncThunk(
    name + "/createHighlight",
    createHighlight
  ),
  removeHighlightAsync: createAsyncThunk(
    name + "/removeHighlight",
    removeHighlight
  ),
  getDynamicLinkAsync: createAsyncThunk(
    name + "/getDynamicLink",
    getDynamicLink
  ),
  getNextContentAsync: createAsyncThunk(
    name + "/getNextContent",
    getNextContent
  ),
  getContentEntityItemsByPlaylistIdAsync: createAsyncThunk(
    name + "/getContentEntityItemsByPlaylistId",
    getContentEntityItemsByPlaylistId
  ),
  searchTrackedAsync: createAsyncThunk(name + "/searchTracked", searchTracked),
  getContentEntityVoiceOversAsync: createAsyncThunk(
    name + "/getContentEntityVoiceOvers",
    getContentEntityVoiceOvers
  ),
};

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearSelectedPlayerContent: (
      state,
      action: PayloadAction<Partial<Content> | undefined>
    ) => {
      state.selectedPlayerContent = {
        ...initialState.selectedPlayerContent,
        content: {
          ...initialState.selectedPlayerContent.content,
          ...action.payload,
        },
      };
    },
    clearNextContent: (state) => {
      state.nextContent = initialState.nextContent;
    },
    clearSearchTracked: (state) => {
      state.searchTracked = initialState.searchTracked;
    },
    setLinkedContent: (
      state,
      action: PayloadAction<{
        uniqueId: string;
        content: Server.ContentEntityOutput;
      }>
    ) => {
      return {
        ...state,
        linkedData: {
          ...state.linkedData,
          [action.payload.uniqueId]: {
            contentIds: [action.payload.content.id],
            contents: [action.payload.content],
            pageNumber: 1,
            pageSize: defaultPageSize,
            hasNextPage: false,
            totalCount: 0,
            status: "idle",
            moreStatus: "idle",
          },
        },
      };
    },
    setSelectedPlayerContent: (state, action: PayloadAction<Content>) => {
      state.selectedPlayerContent.content = action.payload;
    },
    setPlayingChapterId: (state, action: PayloadAction<string | undefined>) => {
      state.selectedPlayerContent.playingChapterId = action.payload;
    },
    setFirstPlayingChapterId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.selectedPlayerContent.firstPlayingChapterId = action.payload;
    },
    setForcedPlayingChapterId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.selectedPlayerContent.forcedPlayingChapterId = action.payload;
    },
    setIsCurrentChapterEnded: (state, action: PayloadAction<boolean>) => {
      state.selectedPlayerContent.isPlayingChapterEnded = action.payload;
    },
    togglePlayer: (state, action: PayloadAction<boolean | undefined>) => {
      setStorageSync({
        key: "PLAYER",
        data: {
          isShown:
            action.payload !== undefined
              ? action.payload
              : !state.player.isShown,
        },
      });

      state.player.isShown =
        action.payload !== undefined ? action.payload : !state.player.isShown;
    },
    changeLoading: (state, action: PayloadAction<boolean>) => {
      state.player.isLoading = action.payload;
    },
    setDuration: (state, action: PayloadAction<number>) => {
      state.player.duration = action.payload;
    },
    play: (state) => {
      state.player.isPlaying = true;
    },
    pause: (state) => {
      state.player.isPlaying = false;
    },
    changePlayingState: (state, action: PayloadAction<boolean | undefined>) => {
      state.player.isPlaying = action.payload;
    },
    seek: (state, action: PayloadAction<number>) => {
      state.player.currentTime = action.payload;
    },
    changeVolume: (state, action: PayloadAction<number>) => {
      state.player.volume = action.payload;
    },
    changePlaybackRate: (state, action: PayloadAction<number>) => {
      state.player.playbackRate = action.payload;
    },
    changeIsAutoStart: (state, action: PayloadAction<boolean>) => {
      state.player.isAutoStart = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.getContentEntityByIdAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        asyncActions.getContentEntityByIdAsync.fulfilled,
        (state, action) => {
          return {
            ...state,
            status: "idle",
            // add or replace with the new data
            data: [
              ...state.data.filter((item) => item.id !== action.payload.id),
              action.payload,
            ],
          };
        }
      )
      .addCase(asyncActions.getContentEntityByIdAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(
        asyncActions.getContentEntityBySelectorsAndSortersAsync.pending,
        (state, action) => {
          return {
            ...state,
            linkedData: {
              ...state.linkedData,
              [action.meta.arg.uniqueId]: {
                ...(state.linkedData[action.meta.arg.uniqueId] ?? {
                  contentIds: [],
                  contents: [],
                  pageNumber: action.meta.arg.input?.pageNumber ?? 1,
                  pageSize:
                    action.meta.arg.input?.pageNumber ?? defaultPageSize,
                  hasNextPage: false,
                  totalCount: 0,
                  moreStatus: "idle",
                }),
                status: "loading",
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getContentEntityBySelectorsAndSortersAsync.fulfilled,
        (state, action) => {
          return {
            ...state,
            status: "success",
            // add or replace old array elements matching with the new data array
            data: [
              ...state.data.filter(
                (item) =>
                  !action.payload.data.data?.some(
                    (newItem) => newItem.id === item.id
                  )
              ),
              ...(action.payload.data.data ?? []),
            ],
            linkedData: {
              ...state.linkedData,
              [action.payload.uniqueId]: {
                contentIds:
                  action.payload.data?.data?.map((d) => d.id ?? "") ?? [],
                contents: action.payload.data?.data ?? [],
                status: "success",
                moreStatus: "idle",
                pageNumber: action.payload.data.currentPage ?? 1,
                pageSize: action.payload.data.pageSize ?? defaultPageSize,
                hasNextPage: action.payload.data.hasNextPage ?? false,
                totalCount: action.payload.data.totalCount ?? 0,
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getContentEntityBySelectorsAndSortersAsync.rejected,
        (state, action) => {
          return {
            ...state,
            linkedData: {
              ...state.linkedData,
              [action.meta.arg.uniqueId]: {
                ...(state.linkedData[action.meta.arg.uniqueId] ?? {
                  contentIds: [],
                  contents: [],
                  pageNumber: action.meta.arg.input?.pageNumber ?? 1,
                  pageSize:
                    action.meta.arg.input?.pageNumber ?? defaultPageSize,
                  hasNextPage: false,
                  totalCount: 0,
                  moreStatus: "idle",
                }),
                status: "failed",
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getMoreContentEntityBySelectorsAndSortersAsync.pending,
        (state, action) => {
          return {
            ...state,
            linkedData: {
              ...state.linkedData,
              [action.meta.arg.uniqueId]: {
                ...(state.linkedData[action.meta.arg.uniqueId] ?? {
                  contentIds: [],
                  contents: [],
                  pageNumber: 1,
                  pageSize: defaultPageSize,
                  hasNextPage: false,
                  totalCount: 0,
                  status: "idle",
                }),
                moreStatus: "loading",
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getMoreContentEntityBySelectorsAndSortersAsync.fulfilled,
        (state, action) => {
          return {
            ...state,
            status: "success",
            // add or replace old array elements matching with the new data array
            data: [
              ...state.data.filter(
                (item) =>
                  !action.payload.data.data?.some(
                    (newItem) => newItem.id === item.id
                  )
              ),
              ...(action.payload.data.data ?? []),
            ],
            linkedData: {
              ...state.linkedData,
              [action.payload.uniqueId]: {
                ...(state.linkedData[action.payload.uniqueId] ?? {
                  contentIds: [],
                  contents: [],
                  pageNumber: action.payload.data.currentPage ?? 1,
                  pageSize: action.payload.data.pageSize ?? defaultPageSize,
                  hasNextPage: action.payload.data.hasNextPage ?? false,
                  totalCount: action.payload.data.totalCount ?? 0,
                  status: "idle",
                }),
                contentIds: [
                  ...state.linkedData[action.payload.uniqueId].contentIds,
                  ...(action.payload.data?.data?.map((d) => d.id ?? "") ?? []),
                ],
                contents: [
                  ...state.linkedData[action.payload.uniqueId].contents,
                  ...(action.payload.data?.data ?? []),
                ],
                moreStatus: "success",
                hasNextPage: action.payload.data.hasNextPage ?? false,
                totalCount: action.payload.data.totalCount ?? 0,
                pageNumber: action.payload.data.currentPage ?? 1,
                pageSize: action.payload.data.pageSize ?? defaultPageSize,
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getMoreContentEntityBySelectorsAndSortersAsync.rejected,
        (state, action) => {
          return {
            ...state,
            linkedData: {
              ...state.linkedData,
              [action.meta.arg.uniqueId]: {
                ...(state.linkedData[action.meta.arg.uniqueId] ?? {
                  contentIds: [],
                  contents: [],
                  pageNumber: 1,
                  pageSize: defaultPageSize,
                  hasNextPage: false,
                  totalCount: 0,
                  status: "idle",
                }),
                moreStatus: "failed",
              },
            },
          };
        }
      )
      .addCase(
        asyncActions.getContentEntitiesProgressAsync.pending,
        (state) => {
          // state.status = 'loading';
        }
      )
      .addCase(
        asyncActions.getContentEntitiesProgressAsync.fulfilled,
        (state, action) => {
          return {
            ...state,
            progresses: [
              ...state.progresses.filter(
                (a) =>
                  action.meta.arg.includes(a.contentEntityId ?? "") == false
              ),
              ...action.meta.arg.map((a) => {
                const found = action.payload.find(
                  (p) => p.contentEntityId === a
                );

                if (found) {
                  return found;
                } else {
                  return {
                    contentEntityId: a,
                    currentProgress: 0,
                  };
                }
              }),
            ],
          };
        }
      )
      .addCase(
        asyncActions.getContentEntitiesProgressAsync.rejected,
        (state) => {
          // state.status = 'failed';
        }
      )
      .addCase(
        asyncActions.getSelectedPlayerContentEntityItemsByIdAsync.pending,
        (state) => {
          state.selectedPlayerContent.chapters.status = "loading";
        }
      )
      .addCase(
        asyncActions.getSelectedPlayerContentEntityItemsByIdAsync.fulfilled,
        (state, action) => {
          state.selectedPlayerContent.chapters = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.getSelectedPlayerContentEntityItemsByIdAsync.rejected,
        (state) => {
          state.selectedPlayerContent.chapters.status = "failed";
        }
      )
      .addCase(asyncActions.getContentEntityItemsByIdAsync.pending, (state) => {
        state.visitedPlayerContent.chapters.status = "loading";
      })
      .addCase(
        asyncActions.getContentEntityItemsByIdAsync.fulfilled,
        (state, action) => {
          state.visitedPlayerContent.chapters = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.getContentEntityItemsByIdAsync.rejected,
        (state) => {
          state.visitedPlayerContent.chapters.status = "failed";
        }
      )
      .addCase(asyncActions.addEventAsync.pending, (state) => {
        state.addEvent.status = "loading";
      })
      .addCase(asyncActions.addEventAsync.fulfilled, (state) => {
        state.addEvent.status = "idle";
      })
      .addCase(asyncActions.addEventAsync.rejected, (state) => {
        state.addEvent.status = "failed";
      })
      .addCase(asyncActions.getInterestsAsync.pending, (state) => {
        state.getInterests.status = "loading";
      })
      .addCase(asyncActions.getInterestsAsync.fulfilled, (state, action) => {
        state.getInterests = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(asyncActions.getInterestsAsync.rejected, (state) => {
        state.getInterests.status = "failed";
      })
      .addCase(asyncActions.setInterestsAsync.pending, (state) => {
        state.setInterests.status = "loading";
      })
      .addCase(asyncActions.setInterestsAsync.fulfilled, (state) => {
        state.setInterests.status = "idle";
      })
      .addCase(asyncActions.setInterestsAsync.rejected, (state) => {
        state.setInterests.status = "failed";
      })
      .addCase(asyncActions.addBookmarkAsync.pending, (state) => {
        state.addBookmark.status = "loading";
      })
      .addCase(asyncActions.addBookmarkAsync.fulfilled, (state) => {
        state.addBookmark.status = "idle";
      })
      .addCase(asyncActions.addBookmarkAsync.rejected, (state) => {
        state.addBookmark.status = "failed";
      })
      .addCase(asyncActions.removeBookmarkAsync.pending, (state) => {
        state.removeBookmark.status = "loading";
      })
      .addCase(asyncActions.removeBookmarkAsync.fulfilled, (state) => {
        state.removeBookmark.status = "idle";
      })
      .addCase(asyncActions.removeBookmarkAsync.rejected, (state) => {
        state.removeBookmark.status = "failed";
      })
      .addCase(asyncActions.getHighlightsAsync.pending, (state) => {
        state.selectedContentHighlights.status = "loading";
      })
      .addCase(asyncActions.getHighlightsAsync.fulfilled, (state, action) => {
        state.selectedContentHighlights = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(asyncActions.getHighlightsAsync.rejected, (state) => {
        state.selectedContentHighlights.status = "failed";
      })
      .addCase(asyncActions.createHighlightAsync.pending, (state) => {
        state.createHighlight.status = "loading";
      })
      .addCase(asyncActions.createHighlightAsync.fulfilled, (state) => {
        state.createHighlight.status = "idle";
      })
      .addCase(asyncActions.createHighlightAsync.rejected, (state) => {
        state.createHighlight.status = "failed";
      })
      .addCase(asyncActions.removeHighlightAsync.pending, (state) => {
        state.removeHighlight.status = "loading";
      })
      .addCase(asyncActions.removeHighlightAsync.fulfilled, (state) => {
        state.removeHighlight.status = "idle";
      })
      .addCase(asyncActions.removeHighlightAsync.rejected, (state) => {
        state.removeHighlight.status = "failed";
      })
      .addCase(asyncActions.getDynamicLinkAsync.pending, (state) => {
        state.dynamicLink.status = "loading";
      })
      .addCase(asyncActions.getDynamicLinkAsync.fulfilled, (state, action) => {
        state.dynamicLink = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(asyncActions.getDynamicLinkAsync.rejected, (state) => {
        state.dynamicLink.status = "failed";
      })
      .addCase(asyncActions.getNextContentAsync.pending, (state) => {
        state.nextContent.status = "loading";
      })
      .addCase(asyncActions.getNextContentAsync.fulfilled, (state, action) => {
        state.nextContent = {
          data: action.payload,
          status: "idle",
        };
      })
      .addCase(asyncActions.getNextContentAsync.rejected, (state) => {
        state.nextContent.status = "failed";
      })
      .addCase(asyncActions.searchTrackedAsync.pending, (state) => {
        state.searchTracked.status = "loading";
      })
      .addCase(asyncActions.searchTrackedAsync.fulfilled, (state, action) => {
        const stateObj = _.reduce(
          state.searchTracked.data,
          function (obj: { [key: string]: Server.ContentEntityOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const dataObj = _.reduce(
          action.payload.data,
          function (obj: { [key: string]: Server.ContentEntityOutput }, param) {
            if (param.id) obj[param.id] = param;

            return obj;
          },
          {}
        );

        const totalObj = {
          ...stateObj,
          ...dataObj,
        };

        state.searchTracked = {
          data: Object.values(totalObj),
          meta: action.payload.meta,
          status: "idle",
        };
      })
      .addCase(asyncActions.searchTrackedAsync.rejected, (state) => {
        state.searchTracked.status = "failed";
      })
      .addCase(
        asyncActions.getContentEntityItemsByPlaylistIdAsync.pending,
        (state) => {
          state.playlistContentItems.status = "loading";
        }
      )
      .addCase(
        asyncActions.getContentEntityItemsByPlaylistIdAsync.fulfilled,
        (state, action) => {
          state.playlistContentItems = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.getContentEntityItemsByPlaylistIdAsync.rejected,
        (state) => {
          state.playlistContentItems.status = "failed";
        }
      )
      .addCase(
        asyncActions.getContentEntityVoiceOversAsync.pending,
        (state) => {
          state.contentVoiceOvers.status = "loading";
        }
      )
      .addCase(
        asyncActions.getContentEntityVoiceOversAsync.fulfilled,
        (state, action) => {
          state.contentVoiceOvers = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.getContentEntityVoiceOversAsync.rejected,
        (state) => {
          state.contentVoiceOvers.status = "failed";
        }
      );
  },
});

export const ContentActions = {
  ...slice.actions,
  ...asyncActions,
};

export default slice.reducer;
